import { useMemo } from "react";
import useMembership from "@/shared/hooks/useMembership";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import { getJwtPayload } from "@/utils/jwt";
import { signInUtm } from "@/features/landing/constants";
import {
  PATHPICK_LANDING_FREE_TRIAL_HOURS,
  PATHPICK_MEMBERSHIP_PATH,
  PATHPICK_SEARCH_PRODUCTS_PATH,
} from "@/features/pathpick/features/landing/constants";

export interface PathPickLandingCtaAction {
  labelKey: string;
  labelOptions?: Record<string, string | number>;
  href: string;
}

export interface PathPickLandingCta {
  isSignedIn: boolean;
  // 멤버십 보유(무료 체험 포함) — 결제/체험을 시작한 뒤인지의 기준.
  hasMembership: boolean;
  // 첫 달 프로모션 대상 여부. 로그인 전에는 첫 결제로 간주한다.
  isFirstSubscription: boolean;
  // 로그인 사용자의 멤버십 응답이 도착하기 전에는 CTA 문구를 확정할 수 없다.
  isReady: boolean;
  // 로그인 + 첫 결제 전에만 하단 플로팅 바를 띄운다.
  // 플로팅 바가 뜨는 동안에는 FAQ 아래 CTA를 감춰 두 CTA가 겹치지 않게 한다.
  showFloatingBar: boolean;
  primary: PathPickLandingCtaAction;
  floating: PathPickLandingCtaAction;
  // 헤더 "서비스로 이동" 목적지 — 가입 전이면 멤버십, 가입 후면 상품 찾기.
  serviceHref: string;
  // 플랜 카드 CTA 목적지 — 로그인 전이면 로그인, 로그인 후면 멤버십 페이지.
  planCtaHref: string;
}

/**
 * 패스픽 랜딩의 모든 CTA(히어로·하단·플로팅 바·헤더·플랜 카드)가 같은 판정을 쓰도록
 * 로그인/멤버십 상태 → 문구·목적지를 한 곳에서 계산한다.
 *
 * 이 페이지는 공개 페이지라 로그아웃 상태로도 열린다. `useMembership`이 내부에서 쓰는
 * `useGetMySelf`는 토큰이 없으면 SWR 키가 null이라 요청 자체가 나가지 않으므로 그대로 호출해도 된다.
 */
export default function usePathPickLandingCta(): PathPickLandingCta {
  const { calcNavigateTo } = useRouterHelper();
  const {
    hasPathPickMembership,
    isMembershipLoading,
    isFirstPathPickPayment,
    hasUsedFreeTrial,
  } = useMembership();

  // 랜딩과 같은 방식 — 쿠키 JWT의 스코프 유무로 로그인 여부를 본다.
  const isSignedIn = Boolean(getJwtPayload()?.scopes[0]?.id);

  return useMemo(() => {
    const signInHref = calcNavigateTo(signInUtm);
    const membershipHref = calcNavigateTo(PATHPICK_MEMBERSHIP_PATH);
    const searchProductsHref = calcNavigateTo(PATHPICK_SEARCH_PRODUCTS_PATH);

    const hasMembership = isSignedIn && hasPathPickMembership;
    const isReady = !isSignedIn || !isMembershipLoading;

    let primary: PathPickLandingCtaAction;
    if (!isSignedIn) {
      primary = {
        labelKey: "path_pick_landing:cta.sign_in_start",
        href: signInHref,
      };
    } else if (hasMembership) {
      primary = {
        labelKey: "path_pick_landing:cta.start",
        href: searchProductsHref,
      };
    } else {
      primary = {
        labelKey: "path_pick_landing:cta.trial",
        href: membershipHref,
      };
    }

    return {
      isSignedIn,
      hasMembership,
      // 로그인 전에는 첫 결제로 간주해 첫 달 할인가를 보여준다.
      isFirstSubscription: isSignedIn ? isFirstPathPickPayment : true,
      isReady,
      showFloatingBar: isSignedIn && isReady && !hasMembership,
      primary,
      floating: {
        // 체험을 이미 소진했다면 "48시간" 문구는 사실이 아니다 — 일반 체험 문구로 낮춘다.
        labelKey: hasUsedFreeTrial
          ? "path_pick_landing:cta.trial"
          : "path_pick_landing:cta.trial_hours",
        labelOptions: { hours: PATHPICK_LANDING_FREE_TRIAL_HOURS },
        href: membershipHref,
      },
      serviceHref: hasMembership ? searchProductsHref : membershipHref,
      planCtaHref: isSignedIn ? membershipHref : signInHref,
    };
  }, [
    calcNavigateTo,
    isSignedIn,
    hasPathPickMembership,
    isMembershipLoading,
    isFirstPathPickPayment,
    hasUsedFreeTrial,
  ]);
}
